import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ShipStatusTracker from './ship-status-tracker';
import { VesselSection, VESSEL_DATA } from './types/vessel';

type DeckType = 'upper' | 'lower' | `middle${number}`;

interface DeckSections {
  upper: VesselSection[];
  lower?: VesselSection[];
  [key: `middle${number}`]: VesselSection[];
}

interface InteractiveShipHullProps {
  upperDeckImage: string;
  bottomDeckImage: string;
  selectedVessel: string;
  deckSections: DeckSections;
  riggingSections: VesselSection[];
  onDeckUpdate: (deckType: DeckType, sections: VesselSection[]) => void;
  onRiggingUpdate: (sections: VesselSection[]) => void;
  onAllDecksUpdate: (deckSections: DeckSections) => void;
}

const InteractiveShipHull: React.FC<InteractiveShipHullProps> = ({ 
  upperDeckImage, 
  bottomDeckImage,
  selectedVessel,
  deckSections,
  riggingSections,
  onDeckUpdate,
  onRiggingUpdate,
  onAllDecksUpdate
}) => {
  const [selectedDeck, setSelectedDeck] = useState<number>(0);
  const [selectedSection, setSelectedSection] = useState<{ type: 'hull' | 'rigging', index: number } | null>(null);
  const [hpAdjustment, setHpAdjustment] = useState<number>(0);
  const [flashingSection, setFlashingSection] = useState<{ type: 'hull' | 'rigging', index: number } | null>(null);

  const riggingLabels = ["Foremast", "Main Mast", "Aft Mast"];

  const getDeckType = (deckIndex: number): DeckType => {
    const vessel = VESSEL_DATA[selectedVessel];
    if (vessel.decks === 2) {
      return deckIndex === 0 ? 'upper' : 'lower';
    }
    if (deckIndex === 0) return 'upper';
    if (deckIndex === vessel.decks - 1) return 'lower';
    return `middle${deckIndex}`;
  };

  const handleSectionClick = (index: number, type: 'hull' | 'rigging') => {
    setSelectedSection({ type, index });
  };

  const handleHPAdjustment = (value: number) => {
    if (!selectedSection) return;

    if (selectedSection.type === 'rigging') {
      const newSections = [...riggingSections];
      const currentHP = newSections[selectedSection.index];
      newSections[selectedSection.index] = {
        ...currentHP,
        current: Math.max(0, Math.min(currentHP.max, currentHP.current + value))
      };
      onRiggingUpdate(newSections);
    } else {
      // Hull section
      const deckType = getDeckType(selectedDeck);
      const currentDeckSections = deckSections[deckType] || [];
      const newSections = [...currentDeckSections];
      const currentHP = newSections[selectedSection.index];
      newSections[selectedSection.index] = {
        ...currentHP,
        current: Math.max(0, Math.min(currentHP.max, currentHP.current + value))
      };
      onDeckUpdate(deckType, newSections);
    }
  };

  const handleRandomDamage = () => {
    const vessel = VESSEL_DATA[selectedVessel];
    const allSections = [
      ...deckSections.upper,
      ...Object.entries(deckSections)
        .filter(([key]) => key.startsWith('middle'))
        .flatMap(([_, sections]) => sections as VesselSection[]),
      ...(deckSections.lower || []),
      ...(riggingSections || [])
    ];
    const randomIndex = Math.floor(Math.random() * allSections.length);

    if (randomIndex < deckSections.upper.length) {
      // Upper deck section
      const newSections = [...deckSections.upper];
      newSections[randomIndex] = {
        ...newSections[randomIndex],
        current: Math.max(0, newSections[randomIndex].current - hpAdjustment)
      };
      onDeckUpdate('upper', newSections);
      setSelectedDeck(0);
      setFlashingSection({ type: 'hull', index: randomIndex });
      setSelectedSection({ type: 'hull', index: randomIndex });
    } else if (randomIndex < deckSections.upper.length + Object.entries(deckSections)
      .filter(([key]) => key.startsWith('middle'))
      .reduce((sum, [_, sections]) => sum + (sections as VesselSection[]).length, 0)) {
      // Middle deck section
      let remainingIndex = randomIndex - deckSections.upper.length;
      for (let i = 1; i < vessel.decks - 1; i++) {
        const middleDeckKey = `middle${i}` as DeckType;
        const middleDeckSections = deckSections[middleDeckKey] || [];
        if (remainingIndex < middleDeckSections.length) {
          const newSections = [...middleDeckSections];
          newSections[remainingIndex] = {
            ...newSections[remainingIndex],
            current: Math.max(0, newSections[remainingIndex].current - hpAdjustment)
          };
          onDeckUpdate(middleDeckKey, newSections);
          setSelectedDeck(i);
          setFlashingSection({ type: 'hull', index: remainingIndex });
          setSelectedSection({ type: 'hull', index: remainingIndex });
          break;
        }
        remainingIndex -= middleDeckSections.length;
      }
    } else if (deckSections.lower) {
      // Lower deck section
      const lowerIndex = randomIndex - deckSections.upper.length - 
        Object.entries(deckSections)
          .filter(([key]) => key.startsWith('middle'))
          .reduce((sum, [_, sections]) => sum + (sections as VesselSection[]).length, 0);
      
      // Ensure the index is within bounds
      if (lowerIndex >= 0 && lowerIndex < deckSections.lower.length) {
        const newSections = [...deckSections.lower];
        newSections[lowerIndex] = {
          ...newSections[lowerIndex],
          current: Math.max(0, newSections[lowerIndex].current - hpAdjustment)
        };
        onDeckUpdate('lower', newSections);
        setSelectedDeck(vessel.decks - 1);
        setFlashingSection({ type: 'hull', index: lowerIndex });
        setSelectedSection({ type: 'hull', index: lowerIndex });
      } else {
        // If the index is out of bounds, try dealing damage to rigging instead
        if (riggingSections && riggingSections.length > 0) {
          const riggingIndex = Math.floor(Math.random() * riggingSections.length);
          const newSections = [...riggingSections];
          newSections[riggingIndex] = {
            ...newSections[riggingIndex],
            current: Math.max(0, newSections[riggingIndex].current - hpAdjustment)
          };
          onRiggingUpdate(newSections);
          setFlashingSection({ type: 'rigging', index: riggingIndex });
          setSelectedSection({ type: 'rigging', index: riggingIndex });
        }
      }
    } else if (riggingSections && riggingSections.length > 0) {
      // Rigging section
      const middleDeckSectionsLength = Object.entries(deckSections)
        .filter(([key]) => key.startsWith('middle'))
        .reduce((sum, [_, sections]) => sum + (sections as VesselSection[]).length, 0);
      const lowerDeckLength = (deckSections.lower as VesselSection[] | undefined)?.length ?? 0;
      const riggingIndex = randomIndex - deckSections.upper.length - middleDeckSectionsLength - lowerDeckLength;
      
      // Ensure the index is within bounds
      if (riggingIndex >= 0 && riggingIndex < riggingSections.length) {
        const newSections = [...riggingSections];
        newSections[riggingIndex] = {
          ...newSections[riggingIndex],
          current: Math.max(0, newSections[riggingIndex].current - hpAdjustment)
        };
        onRiggingUpdate(newSections);
        setFlashingSection({ type: 'rigging', index: riggingIndex });
        setSelectedSection({ type: 'rigging', index: riggingIndex });
      }
    }

    // Clear flashing after a delay
    setTimeout(() => setFlashingSection(null), 1000);
  };

  const getColor = (hp: VesselSection, isFlashing: boolean): string => {
    if (isFlashing) return 'rgba(255, 0, 0, 0.5)'; // Flashing red for damage
    if (hp.current <= 0) return 'rgba(128, 0, 0, 0.5)'; // Destroyed
    if (hp.current <= hp.max / 2) return 'rgba(255, 255, 0, 0.5)'; // Damaged
    return 'rgba(0, 255, 0, 0)'; // Normal
  };

  const handleDealHoledDamage = () => {
    if (!selectedSection || selectedSection.type === 'rigging') return;

    const deckType = getDeckType(selectedDeck);
    const currentDeckSections = deckSections[deckType] || [];
    const newSections = [...currentDeckSections];
    const selectedHP = newSections[selectedSection.index];

    if (!selectedHP.hasDealtHoleDamage) {
      newSections[selectedSection.index] = {
        ...selectedHP,
        hasDealtHoleDamage: true,
        current: Math.max(0, selectedHP.current - 40)
      };
      onDeckUpdate(deckType, newSections);
    }
  };

  const handleRepairAll = () => {
    // Repair all deck sections
    const repairDeck = (sections: VesselSection[]) => {
      return sections.map(section => ({
        ...section,
        current: section.max,
        hasDealtHoleDamage: false
      }));
    };

    // Create a new deckSections object with all decks repaired
    const repairedDeckSections: DeckSections = {
      upper: repairDeck(deckSections.upper.map(section => ({ ...section }))),
      ...(deckSections.lower && { 
        lower: repairDeck(deckSections.lower.map(section => ({ ...section }))) 
      }),
      ...Object.fromEntries(
        Object.entries(deckSections)
          .filter(([key]) => key.startsWith('middle'))
          .map(([key, sections]) => [
            key, 
            repairDeck((sections as VesselSection[]).map(section => ({ ...section })))
          ])
      )
    };

    // Update all decks at once
    onAllDecksUpdate(repairedDeckSections);

    // Repair all rigging sections
    const repairedRigging = riggingSections.map(section => ({
      ...section,
      current: section.max
    }));
    onRiggingUpdate(repairedRigging);
  };

  const handleDeckChange = (direction: 'up' | 'down') => {
    const vessel = VESSEL_DATA[selectedVessel];
    const maxDeck = vessel.decks - 1; // Convert to 0-based index

    if (direction === 'up' && selectedDeck > 0) {
      setSelectedDeck(selectedDeck - 1);
    } else if (direction === 'down' && selectedDeck < maxDeck) {
      setSelectedDeck(selectedDeck + 1);
    }
  };

  const getDeckName = (deckIndex: number): string => {
    const vessel = VESSEL_DATA[selectedVessel];
    if (vessel.decks === 2) {
      return deckIndex === 0 ? 'Upper Deck' : 'Lower Deck';
    }
    if (deckIndex === 0) return 'Upper Deck';
    if (deckIndex === vessel.decks - 1) return 'Lower Deck';
    return `Middle Deck ${deckIndex}`;
  };

  const currentDeckType = getDeckType(selectedDeck);
  const currentDeckSections = deckSections[currentDeckType] || [];
  const currentDeckImage = selectedDeck === 0 ? upperDeckImage : bottomDeckImage;

  // Calculate grid dimensions based on sections per deck
  const sectionsPerRow = 2;
  const rows = Math.ceil(currentDeckSections.length / sectionsPerRow);
  const sectionHeight = `${100 / rows}%`;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold">{getDeckName(selectedDeck)}</h2>
          <div className="flex gap-2">
            {selectedDeck > 0 && (
              <Button onClick={() => handleDeckChange('up')}>
                Go to {getDeckName(selectedDeck - 1)}
              </Button>
            )}
            {selectedDeck < VESSEL_DATA[selectedVessel].decks - 1 && (
              <Button onClick={() => handleDeckChange('down')}>
                Go to {getDeckName(selectedDeck + 1)}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-4">
        {/* Rigging sections */}
        {riggingSections && riggingSections.length > 0 && (
          <div className="w-1/5">
            <h3 className="text-sm font-semibold mb-1">Rigging</h3>
            <div className="space-y-2">
              {riggingSections.map((section, index) => (
                <div
                  key={`rigging-${index}`}
                  className={`p-2 border rounded cursor-pointer transition-colors ${
                    selectedSection?.type === 'rigging' && selectedSection.index === index
                      ? 'border-primary'
                      : ''
                  }`}
                  style={{ backgroundColor: getColor(section, flashingSection?.type === 'rigging' && flashingSection.index === index) }}
                  onClick={() => handleSectionClick(index, 'rigging')}
                >
                  <div className="text-center">
                    <div className="text-sm font-medium">{riggingLabels[index] || `Mast ${index + 1}`}</div>
                    <div className="text-xs">{section.current}/{section.max} HP</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Hull sections */}
        <div className="flex-1 relative">
          <img
            src={currentDeckImage}
            alt={`${getDeckName(selectedDeck)} View`}
            className="w-full h-auto"
          />
          <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${sectionsPerRow}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }}>
            {currentDeckSections.map((section, index) => (
              <div
                key={`hull-${index}`}
                className={`border cursor-pointer transition-colors ${
                  selectedSection?.type === 'hull' && selectedSection.index === index
                    ? 'border-primary'
                    : ''
                }`}
                style={{ backgroundColor: getColor(section, flashingSection?.type === 'hull' && flashingSection.index === index) }}
                onClick={() => handleSectionClick(index, 'hull')}
              >
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <div className="text-sm font-medium">Section {index + 1}</div>
                    <div className="text-xs">{section.current}/{section.max} HP</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="w-64 space-y-4">
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">HP Adjustment</h3>
            <Input
              type="number"
              value={hpAdjustment}
              onChange={(e) => setHpAdjustment(Number(e.target.value))}
              placeholder="Enter HP amount"
            />
            <div className="flex gap-2">
              <Button onClick={() => handleHPAdjustment(hpAdjustment)} className="flex-1">
                Add HP
              </Button>
              <Button onClick={() => handleHPAdjustment(-hpAdjustment)} className="flex-1">
                Remove HP
              </Button>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Damage Controls</h3>
            <Button onClick={handleRandomDamage} className="w-full">
              Deal Random Damage
            </Button>
            <Button 
              onClick={handleDealHoledDamage}
              disabled={!selectedSection || selectedSection.type === 'rigging'}
              className="w-full"
            >
              Deal Hole Damage
            </Button>
            <Button onClick={handleRepairAll} className="w-full">
              Repair All
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InteractiveShipHull;